import React, { useState, MouseEvent } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { authLogout } from 'store/authSlice';
import { setVisiblePopUp } from 'store/uiSlice';
import { clearLocationsOnLogOut } from 'store/queriesSlicer';
import logo from 'assets/icons/Planoo-logo.svg';
import Popup from 'components/Popup';
import Auth from 'components/Auth';
import { RootState, AppDispatch } from 'store/store';

interface MenuItemType {
  label: string;
  path: string;
}

export default function AppHeader(): React.ReactElement {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { email } = useSelector((state: RootState) => state.auth);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const menuItems: MenuItemType[] = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'How it works', path: '/how-it-works' },
    { label: 'Planoo', path: '/app' },
    { label: 'Contact', path: '/contact' },
  ];

  const handleMenuClick = (path: string): void => {
    navigate(path);
    setDrawerOpen(false);
  };

  const handleLogout = async (): Promise<void> => {
    dispatch(authLogout())
      .unwrap()
      .then(() => {
        dispatch(clearLocationsOnLogOut());
        navigate('/');
      });
  };

  const handleClick = (event: MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleLogoutClick = (): void => {
    handleClose();
    handleLogout();
  };

  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: '#fff', boxShadow: 1 }}>
        <Toolbar sx={{ justifyContent: 'space-between', px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src={logo}
              alt="Planoo"
              sx={{ height: 45, cursor: 'pointer', mr: 5 }}
              onClick={() => navigate('/')}
            />
          </Box>

          {!isMobile && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 4,
                flexGrow: 1,
              }}
            >
              {menuItems.map(({ label, path }) => (
                <MenuItem
                  key={label}
                  onClick={() => handleMenuClick(path)}
                  sx={{
                    color: location.pathname === path ? '#219653' : '#333',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    textDecoration:
                      location.pathname === path ? 'underline' : 'none',
                    textUnderlineOffset: '4px',
                    textDecorationThickness: '2px',
                    '&:hover': {
                      color: '#219653',
                      textDecoration: 'underline',
                      textUnderlineOffset: '4px',
                      textDecorationThickness: '2px',
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  {label}
                </MenuItem>
              ))}
            </Box>
          )}

          {isMobile ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                sx={{ cursor: 'pointer', mr: 2, color: '#000' }}
                onClick={
                  email ? handleClick : () => dispatch(setVisiblePopUp(true))
                }
              >
                {email ? `@${email.split('@')[0]}` : 'Login'}
              </Typography>
              <IconButton edge="end" onClick={() => setDrawerOpen(true)}>
                <MenuIcon />
              </IconButton>
            </Box>
          ) : (
            <Typography
              onClick={
                email ? handleClick : () => dispatch(setVisiblePopUp(true))
              }
              sx={{
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: 500,
                color: '#219653',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {email ? `@${email.split('@')[0]}` : 'Login'}
            </Typography>
          )}
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleLogoutClick}>Logout</MenuItem>
      </Menu>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box
          sx={{ width: 250 }}
          role="presentation"
          onClick={() => setDrawerOpen(false)}
        >
          <List>
            {menuItems.map(({ label, path }) => (
              <ListItem
                //button
                key={label}
                onClick={() => handleMenuClick(path)}
              >
                <ListItemText primary={label} />
              </ListItem>
            ))}
            {email && (
              <ListItem onClick={handleLogout}>
                <ListItemText primary="Logout" />
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>

      <Popup
        // popupVisible={popupVisible}
        // setPopupVisible={setPopupVisible}
        width="500px"
      >
        <Auth />
      </Popup>
    </>
  );
}
